import React, { useState } from 'react'
import { Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel } from '@mui/material'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { formatBigMoney, formatDateYYYYMMDD, formatMoney } from '../../../packages/utils/numberFormatters'
import { useTranslation } from 'react-i18next'

const descendingComparator = (a, b) => {
    if (b.date === null || a.date === null) return 0

    if (b.date < a.date) {
        return -1
    }
    if (b.date > a.date) {
        return 1
    }
    return 0
}

const getComparator = (order: 'desc' | 'asc') => {
    return order === 'desc' ? (a, b) => descendingComparator(a, b) : (a, b) => -descendingComparator(a, b)
}

const getTableRows = (convertible: ConvertibleEntity) => {
    const formattedConvertibleActions = convertible.convertibleActions.map((convertibleAction) => {
        return {
            id: convertibleAction.id,
            date: convertibleAction.recordDate,
            type: convertibleAction.actionType,
            amount:
                convertibleAction.repayment || convertibleAction.conversionPrice * convertibleAction.convertedShares,
            conversionPrice: convertibleAction.conversionPrice,
        }
    })
    return formattedConvertibleActions
}

function ConvertibleEvents({ convertible }: { convertible: ConvertibleEntity }) {
    const [order, setOrder] = useState<'desc' | 'asc'>('asc')

    const { i18n, t } = useTranslation()
    const locale = i18n.language

    const sortOrder = () => {
        setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    }

    const tableRows = getTableRows(convertible)

    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell sortDirection={order}>
                        <TableSortLabel active direction={order} onClick={() => sortOrder()}>
                            Datum
                        </TableSortLabel>
                    </TableCell>
                    <TableCell>Aktivitet</TableCell>
                    <TableCell>Belopp</TableCell>
                    <TableCell>Konverteringskurs</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {convertible.loanAmount === null && tableRows.length === 0 ? (
                    <TableRow>
                        <TableCell colSpan={5} align='center'>
                            Inga händelser för konvertibeln är registrerade
                        </TableCell>
                    </TableRow>
                ) : null}
                {convertible.loanAmount !== null ? (
                    <TableRow>
                        <TableCell>{formatDateYYYYMMDD(convertible.publicationDate)}</TableCell>
                        <TableCell>Upptagning av lån</TableCell>
                        <TableCell>
                            {formatBigMoney(convertible.loanAmount, { locale, currencySymbol: convertible.currency })}
                        </TableCell>
                        <TableCell>-</TableCell>
                    </TableRow>
                ) : null}
                {tableRows?.sort(getComparator(order)).map((row) => {
                    return (
                        <TableRow key={row.id}>
                            <TableCell>{row.date}</TableCell>
                            <TableCell>{t(`entities.convertible.fields.${row.type}`)}</TableCell>
                            <TableCell>
                                {formatBigMoney(row.amount, { locale, currencySymbol: convertible.currency })}
                            </TableCell>
                            <TableCell>
                                {row.conversionPrice
                                    ? formatMoney(row.conversionPrice, {
                                          minDecimals: 2,
                                          maxDecimals: 5,
                                          locale,
                                      })
                                    : '-'}
                            </TableCell>
                        </TableRow>
                    )
                })}
            </TableBody>
        </Table>
    )
}
export default ConvertibleEvents
